import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import Chatbot from './Chatbot';
import './App.css';

/* ============================================
   SCROLL ANIMATION HOOK
   ============================================ */
function useScrollAnimation() {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const current = ref.current;
    if (current) observer.observe(current);
    return () => { if (current) observer.unobserve(current); };
  }, []);

  return [ref, isVisible];
}

function AnimatedSection({ children, className = '', delay = 0, direction = 'up' }) {
  const [ref, isVisible] = useScrollAnimation();
  const dirClass = direction === 'left' ? 'fade-in-left' : direction === 'right' ? 'fade-in-right' : 'fade-in';

  return (
    <div
      ref={ref}
      className={`${dirClass} ${isVisible ? 'visible' : ''} ${className}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
}

/* ============================================
   MILITARY BRANCH DATA
   ============================================ */
const militaryBranches = [
  { src: '/images/army.svg', label: 'U.S. Army', url: 'https://www.army.mil' },
  { src: '/images/marines.svg', label: 'U.S. Marine Corps', url: 'https://www.marines.mil' },
  { src: '/images/navy.svg', label: 'U.S. Navy', url: 'https://www.navy.mil' },
  { src: '/images/airforce.svg', label: 'U.S. Air Force', url: 'https://www.af.mil' },
  { src: '/images/coastguard.svg', label: 'U.S. Coast Guard', url: 'https://www.uscg.mil' },
  { src: '/images/spaceforce.svg', label: 'U.S. Space Force', url: 'https://www.spaceforce.mil' },
];

/* ============================================
   SVG ICONS
   ============================================ */
const Icons = {
  Shield: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/>
    </svg>
  ),
  Home: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
    </svg>
  ),
  BookOpen: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
    </svg>
  ),
  DollarSign: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" x2="12" y1="2" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
    </svg>
  ),
  Monitor: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" x2="16" y1="21" y2="21"/><line x1="12" x2="12" y1="17" y2="21"/>
    </svg>
  ),
  Utensils: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/><path d="M7 2v20"/><path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7"/>
    </svg>
  ),
  Heart: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>
    </svg>
  ),
  Users: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  Rocket: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>
    </svg>
  ),
  Lightbulb: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/><path d="M9 18h6"/><path d="M10 22h4"/>
    </svg>
  ),
  MapPin: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/>
    </svg>
  ),
  Mail: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
    </svg>
  ),
  ArrowRight: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
    </svg>
  ),
  Plus: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14"/><path d="M12 5v14"/>
    </svg>
  ),
  Menu: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" x2="20" y1="12" y2="12"/><line x1="4" x2="20" y1="6" y2="6"/><line x1="4" x2="20" y1="18" y2="18"/>
    </svg>
  ),
  X: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
    </svg>
  ),
  Facebook: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  ),
  Phone: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
    </svg>
  ),
};

/* ============================================
   NAVIGATION COMPONENT
   ============================================ */
function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const scrollTo = (id) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <nav className={`nav ${scrolled ? 'scrolled' : ''}`} role="navigation" aria-label="Main navigation">
        <div className="nav-inner">
          <div className="nav-logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} role="button" tabIndex={0} aria-label="Go to top">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{ width: '42px', height: '42px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0 }}>
                <img src="/images/mcsg-logo.png" alt="MCSG Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
              <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: '0.95rem', color: scrolled ? 'var(--color-brand-navy)' : '#ffffff', letterSpacing: '-0.02em', transition: 'color 0.3s ease' }}>Mission Critical <span style={{ color: 'var(--color-accent)' }}>Support Group</span></span>
            </div>
          </div>
          <ul className="nav-links">
            <li><a href="#hero" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>Home</a></li>
            <li><a href="/team">Team</a></li>
            <li><a href="/donate">Donate</a></li>
            <li><a href="/blog">Impact Stories</a></li>
            <li><a href="#contact" onClick={(e) => { e.preventDefault(); scrollTo('contact'); }}>Contact</a></li>
          </ul>
          <button className="mobile-menu-btn" onClick={() => setMobileOpen(true)} aria-label="Open menu">
            <Icons.Menu />
          </button>
        </div>
      </nav>

      <div className={`mobile-nav ${mobileOpen ? 'open' : ''}`} role="dialog" aria-modal="true">
        <button className="mobile-close" onClick={() => setMobileOpen(false)} aria-label="Close menu">
          <Icons.X />
        </button>
        <a href="#hero" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); setMobileOpen(false); }}>Home</a>
        <a href="/team" onClick={() => setMobileOpen(false)}>Team</a>
        <a href="/donate" onClick={() => setMobileOpen(false)}>Donate</a>
        <a href="/blog" onClick={() => setMobileOpen(false)}>Impact Stories</a>
        <a href="#contact" onClick={(e) => { e.preventDefault(); scrollTo('contact'); }}>Contact</a>
      </div>
    </>
  );
}

/* ============================================
   HERO SECTION
   ============================================ */
function Hero() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="hero" role="banner" style={{ background: 'linear-gradient(rgba(27,42,74,0.75), rgba(27,42,74,0.75)), url(/images/hero-restoring-hope.webp) center/cover no-repeat', color: '#fff', padding: 'clamp(8rem, 12vw, 12rem) clamp(1.25rem, 3vw, 2.5rem) clamp(4rem, 6vw, 6rem)' }}>
      <div className={`hero-inner ${loaded ? 'hero-loaded' : ''}`} style={{ maxWidth: '1240px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
          {/* Badge */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(197, 165, 90, 0.15)', border: '1px solid rgba(197, 165, 90, 0.3)', borderRadius: '9999px', padding: '0.4rem 1rem', fontSize: '0.78rem', fontWeight: 600, color: '#C5A55A', marginBottom: '1.5rem', opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.6s ease 0.2s' }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#C5A55A' }} />
            <span>501(c)(3) Nonprofit</span>
            <span style={{ width: '1px', height: '12px', background: 'rgba(197, 165, 90, 0.3)' }} />
            <span>Katy, TX</span>
          </div>

          {/* Main heading */}
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2.5rem, 6vw, 4rem)', fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1.1, color: '#ffffff', marginBottom: '1.5rem', opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.6s ease 0.3s' }}>
            Restoring Hope. <span style={{ color: '#C5A55A' }}>Rebuilding Lives.</span>
          </h1>

          {/* Tagline */}
          <p style={{ fontSize: 'clamp(1rem, 2vw, 1.15rem)', color: 'rgba(255,255,255,0.8)', lineHeight: 1.8, maxWidth: '650px', margin: '0 auto 2rem', opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.6s ease 0.5s' }}>
            Honoring U.S. military veterans and supporting survivors of domestic violence in Katy, TX and the greater Houston area through housing, education, meals, and pathways to <strong style={{ color: '#fff' }}>renewed dignity and stability</strong>.
          </p>

          {/* CTA Buttons */}
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap', opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.6s ease 0.7s' }}>
            <a href="/donate" className="btn-primary" style={{ background: 'linear-gradient(135deg, #C5A55A 0%, #b08e3e 100%)' }}>
              Donate Today <Icons.ArrowRight />
            </a>
            <a href="#contact" className="btn-secondary" style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)' }} onClick={(e) => { e.preventDefault(); document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); }}>
              <Icons.Mail /> Get Involved
            </a>
          </div>
        </div>

        {/* Stat cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', marginTop: 'clamp(3rem, 5vw, 5rem)', opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.6s ease 1s' }}>
          {[
            { number: '37K+', label: 'Veterans Homeless on Any Given Night' },
            { number: '10M', label: 'People Affected by Domestic Violence Yearly' },
            { number: '1', label: 'Mission: Restore Hope & Rebuild Lives' },
          ].map((stat, i) => (
            <div key={i} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 'var(--radius-lg)', padding: '1.5rem', textAlign: 'center' }}>
              <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.75rem', fontWeight: 800, color: '#C5A55A', display: 'block', marginBottom: '0.25rem' }}>{stat.number}</span>
              <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.65)' }}>{stat.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


/* ============================================
   OUR IMPACT SECTION
   ============================================ */
function OurImpact() {
  return (
    <section className="section" id="impact">
      <div className="section-inner">
        <AnimatedSection>
          <div className="section-label"><span className="line" /> Our Impact</div>
          <h2 className="section-title">Empowering Veterans & Survivors Through Compassion and Action</h2>
        </AnimatedSection>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginTop: '2rem' }}>
          <AnimatedSection delay={0.1}>
            <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', padding: '2rem', transition: 'all 0.3s ease' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: 'var(--radius-md)', background: 'rgba(197, 165, 90, 0.08)', color: 'var(--color-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem' }}>
                <Icons.Rocket />
              </div>
              <h3>Restore Dignity</h3>
              <p style={{ marginTop: '0.5rem' }}>Providing housing, meals, and essential resources to veterans facing homelessness, helping them regain stability and self-sufficiency.</p>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.2}>
            <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', padding: '2rem', transition: 'all 0.3s ease' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: 'var(--radius-md)', background: 'rgba(197, 165, 90, 0.08)', color: 'var(--color-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem' }}>
                <Icons.Users />
              </div>
              <h3>Protect & Empower</h3>
              <p style={{ marginTop: '0.5rem' }}>Offering safe shelter, advocacy, and life-changing support services for women and children impacted by domestic violence, abuse, or trafficking.</p>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.3}>
            <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', padding: '2rem', transition: 'all 0.3s ease' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: 'var(--radius-md)', background: 'rgba(197, 165, 90, 0.08)', color: 'var(--color-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem' }}>
                <Icons.Lightbulb />
              </div>
              <h3>Build Futures</h3>
              <p style={{ marginTop: '0.5rem' }}>Equipping individuals with education, tech literacy, and financial skills to build sustainable pathways toward independence and thriving communities.</p>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}

/* ============================================
   PROGRAMS SECTION
   ============================================ */
const programs = [
  { icon: <Icons.Home />, title: 'Housing & Shelter', description: 'Providing safe, stable housing for veterans facing homelessness and women and children escaping domestic violence.' },
  { icon: <Icons.BookOpen />, title: 'Education & Literacy', description: 'Offering educational resources and literacy programs to help individuals build knowledge and confidence for a better future.' },
  { icon: <Icons.DollarSign />, title: 'Financial Literacy', description: 'Teaching budgeting, savings, credit management, and financial planning to foster long-term independence and stability.' },
  { icon: <Icons.Monitor />, title: 'Tech Education & Literacy', description: 'Bridging the digital divide with computer skills, software training, and technology access for career readiness.' },
  { icon: <Icons.Utensils />, title: 'Meals & Nutrition', description: 'Providing nutritious meals and food assistance to veterans, families, and individuals in need throughout our community.' },
  { icon: <Icons.Heart />, title: 'Advocacy & Support', description: 'Offering counseling, legal advocacy, and wraparound support services for survivors of domestic violence, abuse, and trafficking.' },
];

function Programs() {
  return (
    <section className="section" id="programs" style={{ background: 'var(--color-bg-secondary)' }}>
      <div className="section-inner">
        <AnimatedSection>
          <div className="section-label"><span className="line" /> Our Programs</div>
          <h2 className="section-title">How We Serve</h2>
          <p className="section-subtitle">
            We provide comprehensive support through six core program areas, each designed to address
            the unique needs of veterans and survivors in our community.
          </p>
        </AnimatedSection>

        <div className="programs-grid">
          {programs.map((program, index) => (
            <AnimatedSection key={index} delay={index * 0.08}>
              <div style={{ display: 'flex', gap: '1.25rem', padding: '1.75rem', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', transition: 'all 0.3s ease' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: 'var(--radius-md)', background: 'rgba(197, 165, 90, 0.08)', color: 'var(--color-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {program.icon}
                </div>
                <div>
                  <h3 style={{ marginBottom: '0.35rem' }}>{program.title}</h3>
                  <p style={{ fontSize: '0.9rem' }}>{program.description}</p>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================================
   FAQ SECTION
   ============================================ */
const faqData = [
  {
    q: 'What does Mission Critical Support Group do?',
    a: 'Mission Critical Support Group (MCSG) is a 501(c)(3) nonprofit that honors and supports U.S. military veterans, including those facing homelessness, by providing housing, education, meals, tech education, financial literacy, and pathways to renewed dignity. We also protect and empower women and children impacted by domestic violence, abuse, or trafficking in the Katy, TX and greater Houston area.',
  },
  {
    q: 'Is Mission Critical Support Group a nonprofit?',
    a: 'Yes! Mission Critical Support Group is a registered 501(c)(3) nonprofit organization (EIN: 39-3059429). All donations are tax-deductible to the extent allowed by law.',
  },
  {
    q: 'Where is MCSG located?',
    a: 'We are located at 4031 FM 1463 RD STE 40, Katy, TX 77494, in the Cardiff neighborhood of Fort Bend County. We serve the greater Houston area including Katy, Fulshear, Harris County, and Fort Bend County.',
  },
  {
    q: 'How can I donate to Mission Critical Support Group?',
    a: 'Visit our Donate page to make a tax-deductible contribution. As a registered 501(c)(3) nonprofit (EIN: 39-3059429), every dollar goes directly toward providing housing, meals, education, and advocacy for veterans and survivors in Katy, TX and surrounding communities.',
  },
  {
    q: 'How can I volunteer with MCSG?',
    a: 'We welcome volunteers in the Katy, Fulshear, and greater Houston area! Whether you want to help with meal preparation, mentoring, administrative support, or event coordination, there are many ways to get involved. Contact us at contact@mcsg.org or through our website to learn about current volunteer opportunities.',
  },
  {
    q: 'Who does MCSG serve?',
    a: 'We serve U.S. military veterans (including those experiencing homelessness), women and children impacted by domestic violence, abuse, or trafficking, and community members in need of essential support services in the greater Houston area, including Katy, Fulshear, and surrounding Fort Bend and Harris County communities.',
  },
];

function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section className="section" id="faq">
      <div className="section-inner">
        <AnimatedSection>
          <div className="section-label"><span className="line" /> Common Questions</div>
          <h2 className="section-title">Frequently Asked Questions</h2>
          <p className="section-subtitle">
            Everything you need to know about Mission Critical Support Group and how we serve our community.
          </p>
        </AnimatedSection>

        <div style={{ maxWidth: '800px' }}>
          {faqData.map((item, index) => (
            <AnimatedSection key={index} delay={index * 0.05}>
              <div style={{ borderBottom: '1px solid var(--color-border)', overflow: 'hidden' }}>
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  aria-expanded={openIndex === index}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '1.25rem 0', background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-display)', fontSize: '1.05rem', fontWeight: 600, color: 'var(--color-text)', textAlign: 'left', letterSpacing: '-0.01em' }}
                >
                  {item.q}
                  <span style={{ transform: openIndex === index ? 'rotate(45deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease', flexShrink: 0, marginLeft: '1rem', color: 'var(--color-accent)' }}>
                    <Icons.Plus />
                  </span>
                </button>
                <div style={{ maxHeight: openIndex === index ? '300px' : '0', overflow: 'hidden', transition: 'max-height 0.4s ease' }}>
                  <p style={{ paddingBottom: '1.25rem', fontSize: '0.95rem', lineHeight: 1.75, color: 'var(--color-text-secondary)' }}>{item.a}</p>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================================
   DONATE SECTION (Homepage)
   ============================================ */
function Donate() {
  return (
    <section className="section" id="donate" style={{ background: 'var(--color-bg-secondary)' }}>
      <div className="section-inner">
        <AnimatedSection>
          <div className="section-label"><span className="line" /> Support Our Mission</div>
          <h2 className="section-title">Help Us Serve Those Who Served</h2>
          <p className="section-subtitle">
            As a 501(c)(3) nonprofit, Mission Critical Support Group relies on the generosity
            of donors and supporters to provide essential services to veterans and survivors.
            Every contribution makes a direct impact.
          </p>
        </AnimatedSection>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
          <AnimatedSection delay={0.1}>
            <div style={{ background: 'var(--color-surface)', border: '2px solid var(--color-accent)', borderRadius: 'var(--radius-xl)', padding: '2.25rem', position: 'relative' }}>
              <div style={{ position: 'absolute', top: '-1px', right: '1.5rem', background: 'var(--color-accent)', color: '#fff', fontSize: '0.72rem', fontWeight: 700, padding: '0.3rem 0.75rem', borderRadius: '0 0 var(--radius-sm) var(--radius-sm)', letterSpacing: '0.03em' }}>FEATURED</div>
              <div style={{ width: '48px', height: '48px', borderRadius: 'var(--radius-md)', background: 'rgba(197, 165, 90, 0.08)', color: 'var(--color-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem' }}>
                <Icons.Heart />
              </div>
              <h3>Monthly Supporter</h3>
              <p style={{ margin: '0.5rem 0 1.5rem', fontSize: '0.9rem' }}>Become a recurring donor to help sustain our programs year-round. Monthly supporters make long-term planning possible.</p>
              <a href="/donate" className="btn-primary">
                Support Monthly <Icons.ArrowRight />
              </a>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.2}>
            <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-xl)', padding: '2.25rem' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: 'var(--radius-md)', background: 'rgba(197, 165, 90, 0.08)', color: 'var(--color-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem' }}>
                <Icons.Rocket />
              </div>
              <h3>One-Time Donation</h3>
              <p style={{ margin: '0.5rem 0 1.5rem', fontSize: '0.9rem' }}>Make a tax-deductible one-time contribution. Every dollar goes directly toward housing, meals, education, and advocacy programs.</p>
              <a href="/donate" className="btn-primary">
                Donate Now <Icons.ArrowRight />
              </a>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.3}>
            <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-xl)', padding: '2.25rem' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: 'var(--radius-md)', background: 'rgba(197, 165, 90, 0.08)', color: 'var(--color-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem' }}>
                <Icons.Shield />
              </div>
              <h3>Corporate Partnership</h3>
              <p style={{ margin: '0.5rem 0 1.5rem', fontSize: '0.9rem' }}>Partner with MCSG to invest in veteran support and community protection. Corporate partners receive recognition and event access.</p>
              <a href="mailto:contact@mcsg.org?subject=Corporate Partnership Inquiry" className="btn-primary">
                Get in Touch <Icons.ArrowRight />
              </a>
            </div>
          </AnimatedSection>
        </div>

        <AnimatedSection delay={0.2}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', padding: '1.5rem 2rem', background: 'rgba(197, 165, 90, 0.04)', border: '1px solid rgba(197, 165, 90, 0.15)', borderRadius: 'var(--radius-lg)', marginTop: '3rem', maxWidth: '800px' }}>
            <div style={{ flexShrink: 0, color: 'var(--color-accent)', marginTop: '2px' }}><Icons.Lightbulb /></div>
            <p style={{ fontSize: '0.9rem', lineHeight: 1.7 }}>
              Mission Critical Support Group is a registered <strong style={{ color: 'var(--color-text)' }}>501(c)(3) nonprofit organization</strong> (EIN: 39-3059429).
              All donations are tax-deductible to the extent allowed by law.
            </p>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}


/* ============================================
   CONTACT SECTION
   ============================================ */
function Contact() {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // TODO: Connect to Formspree or similar service
    // For now, open mailto
    const subject = encodeURIComponent(formData.subject || 'Website Inquiry');
    const body = encodeURIComponent(`Name: ${formData.name}\nEmail: ${formData.email}\n\n${formData.message}`);
    window.location.href = `mailto:contact@mcsg.org?subject=${subject}&body=${body}`;

    setSubmitted(true);
    setFormData({ name: '', email: '', subject: '', message: '' });
    setTimeout(() => setSubmitted(false), 5000);
    setSubmitting(false);
  };

  return (
    <section className="section" id="contact">
      <div className="section-inner">
        <AnimatedSection>
          <div className="section-label"><span className="line" /> Get In Touch</div>
          <h2 className="section-title">Contact Us</h2>
          <p className="section-subtitle">
            Have questions about our programs, want to volunteer, or interested in partnering with us?
            We'd love to hear from you.
          </p>
        </AnimatedSection>

        <div className="contact-grid">
          <AnimatedSection direction="left">
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="contact-name">Name</label>
                  <input id="contact-name" type="text" placeholder="Your full name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label htmlFor="contact-email">Email</label>
                  <input id="contact-email" type="email" placeholder="you@example.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="contact-subject">Subject</label>
                <input id="contact-subject" type="text" placeholder="What's this about?" value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} />
              </div>
              <div className="form-group">
                <label htmlFor="contact-message">Message</label>
                <textarea id="contact-message" placeholder="Tell us how we can help..." rows="5" value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} required />
              </div>
              <button type="submit" className="btn-primary" style={{ alignSelf: 'flex-start' }} disabled={submitting}>
                {submitted ? 'Message Sent!' : submitting ? 'Sending...' : 'Send Message'} {!submitted && !submitting && <Icons.ArrowRight />}
              </button>
            </form>
          </AnimatedSection>

          <AnimatedSection direction="right">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: 'var(--radius-md)', background: 'rgba(197, 165, 90, 0.08)', color: 'var(--color-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icons.MapPin />
                </div>
                <div>
                  <h4 style={{ marginBottom: '0.25rem' }}>Visit Us</h4>
                  <p style={{ fontSize: '0.9rem' }}>4031 FM 1463 RD STE 40<br />Katy, TX 77494</p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: 'var(--radius-md)', background: 'rgba(197, 165, 90, 0.08)', color: 'var(--color-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icons.Mail />
                </div>
                <div>
                  <h4 style={{ marginBottom: '0.25rem' }}>Email Us</h4>
                  <p style={{ fontSize: '0.9rem' }}><a href="mailto:contact@mcsg.org" style={{ color: 'var(--color-accent)' }}>contact@mcsg.org</a></p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: 'var(--radius-md)', background: 'rgba(197, 165, 90, 0.08)', color: 'var(--color-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icons.Lightbulb />
                </div>
                <div>
                  <h4 style={{ marginBottom: '0.25rem' }}>Areas We Serve</h4>
                  <p style={{ fontSize: '0.9rem' }}>Katy, TX &bull; Fulshear, TX &bull; Houston, TX<br />Fort Bend County &bull; Harris County</p>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}


/* ============================================
   CTA SECTION
   ============================================ */
function CTA() {
  return (
    <section style={{ background: 'linear-gradient(135deg, #1B2A4A 0%, #2a3f6a 100%)', padding: 'clamp(4rem, 6vw, 6rem) clamp(1.25rem, 3vw, 2.5rem)', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
      <div style={{ maxWidth: '700px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <AnimatedSection>
          <h2 style={{ color: '#ffffff', marginBottom: '1rem' }}>
            Ready to <span className="gradient-text" style={{ background: 'linear-gradient(135deg, #C5A55A, #d4b96f)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Make a Difference?</span>
          </h2>
        </AnimatedSection>
        <AnimatedSection delay={0.1}>
          <p style={{ color: 'rgba(255,255,255,0.75)', marginBottom: '2rem' }}>
            Join our mission to support veterans and protect survivors. Whether through donation,
            volunteering, or spreading the word, every action counts.
          </p>
        </AnimatedSection>
        <AnimatedSection delay={0.2}>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="/donate" className="btn-primary" style={{ background: 'linear-gradient(135deg, #C5A55A 0%, #b08e3e 100%)' }}>
              Donate Now <Icons.ArrowRight />
            </a>
            <a href="mailto:contact@mcsg.org" className="btn-secondary" style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)' }}>
              <Icons.Mail /> Contact Us
            </a>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

/* ============================================
   FOOTER
   ============================================ */
function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="footer" role="contentinfo">
      <div className="footer-inner">
        <div className="footer-top">
          <div className="footer-brand">
            <div className="nav-logo">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{ width: '38px', height: '38px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0 }}>
                  <img src="/images/mcsg-logo.png" alt="MCSG Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
                <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: '0.95rem', color: 'var(--color-brand-navy)', letterSpacing: '-0.02em' }}>Mission Critical <span style={{ color: 'var(--color-accent)' }}>Support Group</span></span>
              </div>
            </div>
            <p>
              Honoring U.S. military veterans and supporting survivors of domestic violence
              through housing, education, meals, and advocacy in Katy, TX and surrounding communities.
            </p>
          </div>

          <div className="footer-col">
            <h4>Programs</h4>
            <ul>
              <li><a href="#programs">Housing & Shelter</a></li>
              <li><a href="#programs">Education & Literacy</a></li>
              <li><a href="#programs">Financial Literacy</a></li>
              <li><a href="#programs">Tech Education</a></li>
              <li><a href="#programs">Meals & Nutrition</a></li>
              <li><a href="#programs">Advocacy & Support</a></li>
            </ul>
          </div>

          <div className="footer-col">
            <h4>Organization</h4>
            <ul>
              <li><a href="#impact">About Us</a></li>
              <li><a href="/team">Our Team</a></li>
              <li><a href="/donate">Donate</a></li>
              <li><a href="/blog">Impact Stories</a></li>
              <li><a href="#contact">Contact</a></li>
              <li><a href="#faq">FAQ</a></li>
            </ul>
          </div>

          <div className="footer-col">
            <h4>Location</h4>
            <ul>
              <li style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', color: 'var(--color-text-secondary)' }}>
                <span style={{ flexShrink: 0, marginTop: '2px' }}><Icons.MapPin /></span>
                <span>4031 FM 1463 RD STE 40,<br />Katy, TX 77494</span>
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-text-secondary)', marginTop: '0.5rem' }}>
                <span style={{ flexShrink: 0 }}><Icons.Mail /></span>
                <a href="mailto:contact@mcsg.org">contact@mcsg.org</a>
              </li>
            </ul>
            <div style={{ marginTop: '1.25rem' }}>
              <p style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--color-text-muted)', marginBottom: '0.6rem', textAlign: 'center' }}>Proudly Serving All Branches</p>
              <div style={{ display: 'flex', gap: '0.35rem', justifyContent: 'center' }}>
                {militaryBranches.map((branch, i) => (
                  <a key={i} href={branch.url} target="_blank" rel="noopener noreferrer" title={branch.label} style={{ width: '36px', height: '36px', borderRadius: '50%', overflow: 'hidden', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(197, 165, 90, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', flexShrink: 0 }}>
                    <img src={branch.src} alt={branch.label} style={{ width: '30px', height: '30px', objectFit: 'contain' }} />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <p>&copy; {currentYear} Mission Critical Support Group. All rights reserved. 501(c)(3) Nonprofit Organization. (EIN: 39-3059429)</p>
          <div className="footer-bottom-links">
            <a href="/privacy">Privacy Policy</a>
            <span className="footer-bottom-divider">|</span>
            <a href="/terms">Terms of Service</a>
            <span className="footer-bottom-divider">|</span>
            <a className="footer-credit" href="https://www.mysticmediafilm.com" target="_blank" rel="noopener noreferrer">Website designed by Mystic Media Film</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ============================================
   MAIN APP
   ============================================ */
function App() {
  return (
    <div className="App">
      <Helmet>
        <title>Mission Critical Support Group | Veteran & Domestic Violence Support Nonprofit in Katy, TX</title>
        <meta name="description" content="Mission Critical Support Group is a 501(c)(3) nonprofit in Katy, TX providing housing, education, meals, financial literacy, tech training, and advocacy for homeless veterans and survivors of domestic violence. EIN: 39-3059429." />
        <link rel="canonical" href="https://www.missioncriticalsupportgroup.com/" />
        <meta name="keywords" content="veteran support Katy TX, homeless veteran assistance Houston, domestic violence shelter Katy Texas, nonprofit veteran housing, financial literacy veterans, tech education veterans, meals for veterans, 501c3 nonprofit Katy TX, Mission Critical Support Group, MCSG" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.missioncriticalsupportgroup.com/" />
        <meta property="og:title" content="Mission Critical Support Group | Veteran & Domestic Violence Support Nonprofit in Katy, TX" />
        <meta property="og:description" content="501(c)(3) nonprofit honoring U.S. military veterans and supporting survivors of domestic violence through housing, education, meals, and advocacy in Katy, TX." />
        <meta property="og:site_name" content="Mission Critical Support Group" />
        <meta property="og:locale" content="en_US" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Mission Critical Support Group | Veteran & Domestic Violence Support Nonprofit" />
        <meta name="twitter:description" content="Providing housing, education, meals, and advocacy for veterans and survivors of domestic violence in Katy, TX. 501(c)(3) nonprofit." />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqData.map(item => ({
          "@type": "Question",
          "name": item.q,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": item.a
          }
        }))
      }) }} />
      <Navigation />
      <main>
        <Hero />
        <OurImpact />
        <Programs />
        <FAQ />
        <Donate />
        <Contact />
        <CTA />
      </main>
      <Footer />
      <Chatbot />
    </div>
  );
}

export { CTA, Footer };
export default App;
